import { useEffect, useMemo, useRef, useState } from 'react';
import { useKept } from '@/lib/keptScroll';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useChats } from '@/data/store';
import { askDrc, drcChatGroup, loadDrcChat, moveDrcChat, plainAnswer, type DrcChatSummary, type DrcProject } from '@/lib/drc';
import { askActions, loadAsk, useAsk } from '@/lib/askStore';
import { AskChatMenu, AskProjectMenu } from '@/components/AskMenus';
import { DrcAvatar } from '@/components/Avatar';
import { DrcAnswer } from '@/components/DrcAnswer';
import { ArrowUpIcon, ChevronIcon, CopyIcon, EditIcon, FolderIcon, MoreIcon, NewChatIcon, PlusIcon, RetryIcon, SearchIcon, SparkIcon, StarIcon, StopIcon } from '@/components/Icons';
import { useOverlay } from '@/components/Overlay';
import { ReportButton } from '@/components/ReportButton';
import { DutySwitch } from '@/components/DutySwitch';
import { STAFF_ASSISTANT_NAME } from '@/lib/assistant';

// Ask on a computer, laid out like Sentinel's Ask page and the Claude app (Gary, 15 Oct):
// the person's Dr C chats on the left (the same history as Sentinel, Ask chats only), and
// in the middle a greeting with the ask box centred for a new chat, then one centred column
// of questions and answers with the ask box fixed below. The open chat is ?c= in the address.

const SUGGESTIONS = ['My bookings tomorrow', 'Unpaid invoices in my territory', 'What have I invoiced this month?'];
const SAVED = 'chats:drc-saved';

const ago = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();
const SAMPLE_TURN = {
  question: 'Who owes us more than R2,000? Show me the invoices.',
  answer: 'Here are the 4 unpaid invoices over R2,000, largest first. Total owed: R11,170.50.\n\n| Invoice | Customer | Territory | Invoice date | Due date | Status | Balance |\n|---|---|---|---|---|---|---|\n| INV-ZA-025247 | Larry Katz | Southern Suburbs | 9 Oct 2026 | 12 Oct 2026 | Overdue | R3,450.00 |\n| INV-ZA-025262 | ShipMed, Ear, Nose and Throat Centre | Umhlanga & Durban North | 12 Oct 2026 | 15 Oct 2026 | Sent | R2,980.00 |\n| INV-ZA-025136 | Arlene Tobin | Atlantic Seaboard | 10 Sep 2026 | 13 Sep 2026 | Overdue | R2,708.75 |\n| INV-ZA-025271 | Anton Rose | Southern Suburbs | 13 Oct 2026 | 16 Oct 2026 | Sent | R2,031.75 |\n\nThe full list is in Sentinel: https://za.app.cleanmybed.com/invoices?status=unpaid&sort=balance_desc&territory=all&from=2026-09-01\n\nA reminder goes out automatically 3 days after the due date.',
};
export const SAMPLE_PROJECTS: DrcProject[] = [{ id: 'p1', name: 'Month-end', description: null }, { id: 'p2', name: 'Tokai leads', description: null }];
export const SAMPLE_ASK: DrcChatSummary[] = [
  { id: 's1', title: 'Bookings tomorrow', updated_at: ago(1), lastQuestion: 'Which of my bookings tomorrow are not confirmed yet?', questions: 3 },
  { id: 's2', title: 'Unpaid invoices', updated_at: ago(3), starred_at: ago(2), lastQuestion: 'Who owes us more than R2,000?', questions: 2 },
  { id: 's3', title: 'Sarah M. history', updated_at: ago(26), lastQuestion: 'When was Sarah last serviced and what was done?', questions: 1 },
  { id: 's4', title: 'October invoicing', updated_at: ago(80), project_id: 'p1', lastQuestion: 'What have I invoiced this month?', questions: 4 },
  { id: 's5', title: 'Leads in Tokai', updated_at: ago(400), project_id: 'p2', lastQuestion: 'How many Tokai leads came in this month?', questions: 1 },
];

/** With a mouse and keyboard Enter sends; on a phone Return adds a line and the arrow sends, as Sentinel's Ask. */
function enterSends(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function greeting(firstName: string | null | undefined): string {
  const h = new Date().getHours();
  const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  return firstName ? `${part}, ${firstName}` : part;
}

/**
 * Left column as Claude's sidebar (Gary, 18 Oct): New chat and search, then Starred, Projects (each opens to
 * its chats) and Recents by date. Each chat's menu: Star, Rename, Move to project, Delete.
 */
export function DrcHistory() {
  const { session } = useAuth();
  const { live } = useChats();
  const { openSheet } = useOverlay();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const active = params.get('c');
  const { chats, projects } = useAsk();
  // Kept while a result is open, so Back returns to the same results, as in Chats (regression run, 19 Oct).
  const [search, setSearch] = useKept<string>('ask.search', '');
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [openProjects, setOpenProjects] = useState<string[]>([]);
  const [newProject, setNewProject] = useState<string | null>(null);
  const userId = session?.user.id;

  useEffect(() => {
    // Sample chats for the preview, so the list can be judged without a login.
    const load = () => void loadAsk(live && userId ? userId : null, live ? undefined : { chats: SAMPLE_ASK, projects: SAMPLE_PROJECTS });
    load();
    window.addEventListener(SAVED, load);
    return () => window.removeEventListener(SAVED, load);
  }, [live, userId]);

  const term = search.trim().toLowerCase();
  const shown = (chats ?? []).filter((x) => !term || (x.title ?? '').toLowerCase().includes(term) || (x.lastQuestion ?? '').toLowerCase().includes(term));
  const starred = shown.filter((c) => c.starred_at).sort((a, b) => (b.starred_at ?? '').localeCompare(a.starred_at ?? ''));
  const recent = shown.filter((c) => !c.starred_at && !c.project_id);
  // While searching, only projects with a match show, and an empty Projects section hides (mobile review, 19 Oct).
  const visibleProjects = projects.filter((p) => !term || shown.some((c) => c.project_id === p.id) || p.name.toLowerCase().includes(term));
  const groups = useMemo(() => {
    const out: { label: string; items: DrcChatSummary[] }[] = [];
    for (const c of recent) {
      const label = drcChatGroup(c.updated_at);
      const last = out[out.length - 1];
      if (last?.label === label) last.items.push(c);
      else out.push({ label, items: [c] });
    }
    return out;
  }, [recent]);

  function saveTitle(id: string) {
    const title = editText.trim();
    setEditing(null);
    if (title) void askActions.rename(id, title);
  }

  const startRename = (c: DrcChatSummary) => { setEditing(c.id); setEditText(c.title ?? ''); };
  const menu = (c: DrcChatSummary) => openSheet(<AskChatMenu chat={c} onRename={() => startRename(c)} onDeleted={() => { if (c.id === active) navigate('/drc', { replace: true }); }} />);

  function row(c: DrcChatSummary, compact = false) {
    if (editing === c.id) {
      return (
        <input key={c.id} className="chats-input chats-ask-rename" value={editText} maxLength={200} autoFocus aria-label="Chat name"
          onChange={(e) => setEditText(e.target.value)} onBlur={() => saveTitle(c.id)}
          onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(c.id); if (e.key === 'Escape') setEditing(null); }} />
      );
    }
    const sub = c.lastQuestion && c.lastQuestion.trim().toLowerCase() !== (c.title ?? '').trim().toLowerCase() ? c.lastQuestion : '';
    return (
      <div key={c.id} className={`chats-ask-item${compact ? ' chats-ask-item--compact' : ''}${c.id === active ? ' chats-ask-item--active' : ''}`}
        onContextMenu={(e) => { e.preventDefault(); menu(c); }} {...pressHold(() => menu(c))}>
        <button type="button" className="chats-ask-item-open" onClick={() => navigate(`/drc?c=${c.id}`)}>
          {!compact && <span className="chats-ask-item-mark" aria-hidden="true"><SparkIcon /></span>}
          <span className="chats-ask-item-main">
            <span className="chats-ask-item-top">
              <span className="chats-ask-item-title">{c.title || 'Untitled chat'}</span>
              {c.starred_at && compact === false && <span className="chats-ask-item-star" aria-label="Starred"><StarIcon filled /></span>}
              <span className="chats-ask-item-time">{askTime(c.updated_at)}</span>
            </span>
            {!compact && (
              <span className="chats-ask-item-sub">
                <span className="chats-ask-item-q">{sub}</span>
                {(c.questions ?? 0) > 1 && <span className="chats-ask-item-count">{c.questions} questions</span>}
              </span>
            )}
          </span>
        </button>
        <button type="button" className="chats-row-more chats-ask-item-more" aria-label={`More for ${c.title || 'this chat'}`} onClick={() => menu(c)}><MoreIcon /></button>
      </div>
    );
  }

  const projectMenu = (p: DrcProject) => openSheet(
    <AskProjectMenu id={p.id} name={p.name} onRename={() => { setEditing(p.id); setEditText(p.name); }} onNewChat={() => navigate(`/drc?new=1&project=${p.id}`)} />,
  );

  function saveProject() {
    const name = (newProject ?? '').trim();
    setNewProject(null);
    if (name) void askActions.createProject(name).then((p) => { if (p) setOpenProjects((o) => [...o, p.id]); });
  }

  return (
    <>
      <header className="chats-listhead">
        <div className="chats-listhead-row">
          <h1 className="chats-listhead-title">Ask</h1>
          <DutySwitch />
          <ReportButton />
          <button type="button" className="chats-listhead-help" aria-label="New chat" title="New chat" onClick={() => navigate('/drc?new=1')}><NewChatIcon /></button>
        </div>
        <label className="chats-searchbox chats-ask-search">
          <SearchIcon />
          <input type="search" placeholder={`Search your chats with ${STAFF_ASSISTANT_NAME}`} aria-label="Search chats" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </header>
      <main className="chats-screen">
        {chats === null ? null : (
          <>
            {starred.length > 0 && (
              <section>
                <p className="chats-ask-label">Starred</p>
                {starred.map((c) => row(c))}
              </section>
            )}

            {(!term || visibleProjects.length > 0 || newProject !== null) && (
              <section>
                <div className="chats-ask-label chats-ask-label--row">
                  <span>Projects</span>
                  <button type="button" className="chats-ask-label-add" aria-label="New project" title="New project" onClick={() => setNewProject('')}><PlusIcon /></button>
                </div>
                {newProject !== null && (
                  <input className="chats-input chats-ask-rename" placeholder="Project name" aria-label="Project name" maxLength={80} autoFocus value={newProject}
                    onChange={(e) => setNewProject(e.target.value)} onBlur={saveProject}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveProject(); if (e.key === 'Escape') setNewProject(null); }} />
                )}
                {projects.length === 0 && newProject === null && <p className="chats-ask-hint">Keep related chats together. Tap + to start one.</p>}
                {visibleProjects.map((p) => {
                  const inIt = shown.filter((c) => c.project_id === p.id);
                  const open = openProjects.includes(p.id) || (!!term && inIt.length > 0);
                  return (
                    <div key={p.id} className="chats-ask-project">
                      {editing === p.id ? (
                        <input className="chats-input chats-ask-rename" value={editText} maxLength={80} autoFocus aria-label="Project name"
                          onChange={(e) => setEditText(e.target.value)}
                          onBlur={() => { const n = editText.trim(); setEditing(null); if (n) void askActions.renameProject(p.id, n); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditing(null); }} />
                      ) : (
                        <div className="chats-ask-item chats-ask-item--project" onContextMenu={(e) => { e.preventDefault(); projectMenu(p); }} {...pressHold(() => projectMenu(p))}>
                          <button type="button" className="chats-ask-item-open" aria-expanded={open}
                            onClick={() => setOpenProjects((o) => (o.includes(p.id) ? o.filter((x) => x !== p.id) : [...o, p.id]))}>
                            <span className={`chats-ask-chev${open ? ' chats-ask-chev--open' : ''}`} aria-hidden="true"><ChevronIcon /></span>
                            <span className="chats-ask-folder" aria-hidden="true"><FolderIcon /></span>
                            <span className="chats-ask-item-title">{p.name}</span>
                            <span className="chats-ask-item-time">{inIt.length || ''}</span>
                          </button>
                          <button type="button" className="chats-row-more chats-ask-item-more" aria-label={`New chat in ${p.name}`} title="New chat in this project" onClick={() => navigate(`/drc?new=1&project=${p.id}`)}><PlusIcon /></button>
                          <button type="button" className="chats-row-more chats-ask-item-more chats-ask-item-more--second" aria-label={`More for ${p.name}`}
                            onClick={() => projectMenu(p)}><MoreIcon /></button>
                        </div>
                      )}
                      {open && (inIt.length ? inIt.map((c) => row(c, true)) : <p className="chats-ask-hint chats-ask-hint--in">No chats yet. Use + to start one here.</p>)}
                    </div>
                  );
                })}
              </section>
            )}

            {groups.length === 0 && starred.length === 0 && (!term || visibleProjects.length === 0) && (
              <p className="chats-empty">{search ? 'No chats match.' : `No chats yet. Ask ${STAFF_ASSISTANT_NAME} anything.`}</p>
            )}
            {groups.map((g) => (
              <section key={g.label}>
                <p className="chats-ask-label">{g.label}</p>
                {g.items.map((c) => row(c))}
              </section>
            ))}
          </>
        )}
      </main>
    </>
  );
}

/** A finger held on a row opens its menu, as WhatsApp and Claude (iPhone sends no right-click). */
function pressHold(open: () => void) {
  let timer: number | undefined;
  let fired = false;
  const cancel = () => { if (timer) window.clearTimeout(timer); timer = undefined; };
  return {
    onTouchStart: () => { cancel(); fired = false; timer = window.setTimeout(() => { timer = undefined; fired = true; open(); }, 500); },
    onTouchMove: cancel,
    // The menu opened: the lift that follows must not also open the chat.
    onTouchEnd: (e: { preventDefault: () => void }) => { cancel(); if (fired) e.preventDefault(); },
    onTouchCancel: cancel,
  };
}

/** "14:05" today, "Mon" this week, "12 Oct" before that. */
function askTime(at: string): string {
  const d = new Date(at);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (now.getTime() - d.getTime() < 6 * 86_400_000) return d.toLocaleDateString('en-GB', { weekday: 'short' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

interface Turn { question: string; answer: string; step: string | null; error: string | null; done: boolean }

/** Middle: the open Dr C chat, or a new one. */
export function DrcConversation({ phone = false }: { phone?: boolean }) {
  const { profile } = useAuth();
  const { live } = useChats();
  const { toast } = useOverlay();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const active = params.get('c');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const stopper = useRef<AbortController | null>(null);
  const project = params.get('project');
  const { chats: askChats, projects } = useAsk();
  const summary = (askChats ?? []).find((c) => c.id === active) ?? null;
  const inProject = projects.find((p) => p.id === (summary?.project_id ?? project)) ?? null;
  const [renaming, setRenaming] = useState<string | null>(null);
  const { openSheet } = useOverlay();
  const current = useRef<string | null>(active); // the chat these turns belong to
  const box = useRef<HTMLTextAreaElement>(null);
  const thread = useRef<HTMLDivElement>(null);
  const busy = turns.some((t) => !t.done);

  // Open a saved chat, or clear for a new one. A chat this screen just created is already showing.
  useEffect(() => {
    if (active === current.current && (turns.length || !active)) return;
    current.current = active;
    setTurns([]);
    // The preview opens a sample answer with a table and a long link, so phones can be checked (Gary, 18 Oct).
    if (active && !live) { setTurns([{ ...SAMPLE_TURN, step: null, error: null, done: true }]); return; }
    if (!active || !live) return;
    let gone = false;
    setLoading(true);
    void loadDrcChat(active)
      .then((t) => { if (!gone) setTurns(t.map((x) => ({ ...x, step: null, error: null, done: true }))); })
      .catch(() => { if (!gone) setTurns([]); })
      .finally(() => { if (!gone) setLoading(false); });
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, live]);

  useEffect(() => { box.current?.focus(); }, [active]);

  // Follow a streaming answer only while the reader is at the bottom; a new question always goes to the
  // bottom (mobile review, 18 Oct: reading the start of a long answer kept being pulled down).
  const nearBottom = useRef(true);
  useEffect(() => {
    const el = thread.current;
    if (el) el.scrollTop = el.scrollHeight;
    nearBottom.current = true;
  }, [turns.length]);
  useEffect(() => {
    const el = thread.current;
    if (el && nearBottom.current) el.scrollTop = el.scrollHeight;
  }, [turns[turns.length - 1]?.answer]);

  function copy(text: string, done: string) {
    void navigator.clipboard?.writeText(text).then(() => toast(done)).catch(() => toast('Could not copy. Select the text instead.'));
  }

  function patchLast(change: (t: Turn) => Turn) {
    setTurns((all) => {
      const next = all.slice();
      const last = next[next.length - 1];
      if (last) next[next.length - 1] = change(last);
      return next;
    });
  }

  async function send(text: string) {
    const q = text.trim();
    if (!q || busy) return;
    setDraft('');
    setTurns((all) => [...all, { question: q, answer: '', step: 'Thinking', error: null, done: false }]);
    if (!live) {
      patchLast((t) => ({ ...t, step: null, answer: `${STAFF_ASSISTANT_NAME} answers on live chats only.`, done: true }));
      return;
    }
    const stop = new AbortController();
    stopper.current = stop;
    await askDrc({ text: q, drcConversationId: current.current, chatId: null, signal: stop.signal }, (event) => {
      if (event.type === 'conversation' && event.id !== current.current) {
        current.current = event.id;
        // A new chat started inside a project goes in it (Gary, 18 Oct).
        if (project) void moveDrcChat(event.id, project).then(() => window.dispatchEvent(new Event(SAVED))).catch(() => undefined);
        navigate(`/drc?c=${event.id}`, { replace: true });
      } else if (event.type === 'step') patchLast((t) => ({ ...t, step: event.label }));
      else if (event.type === 'text') patchLast((t) => ({ ...t, step: null, answer: t.answer + event.text }));
      else if (event.type === 'error') patchLast((t) => ({ ...t, step: null, error: event.message, done: true }));
      else if (event.type === 'done') patchLast((t) => ({ ...t, step: null, done: true, ...(event.status === 'stopped' ? { error: 'Stopped.' } : {}) }));
    });
    stopper.current = null;
    patchLast((t) => ({ ...t, step: null, done: true }));
    window.dispatchEvent(new Event(SAVED));
  }

  const composer = (
    <form className={`chats-ask-composer${phone && turns.length ? ' chats-ask-composer--row' : ''}`} onSubmit={(e) => { e.preventDefault(); void send(draft); }}>
      <textarea ref={box} className="chats-ask-input" rows={phone ? 1 : 2} maxLength={4000} value={draft} aria-label={`Ask ${STAFF_ASSISTANT_NAME}`}
        placeholder={turns.length ? `Reply to ${STAFF_ASSISTANT_NAME}` : `Ask ${STAFF_ASSISTANT_NAME}`}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && enterSends()) { e.preventDefault(); void send(draft); } }} />
      <div className="chats-ask-foot">
        <span>{busy ? 'Answering...' : turns.length && phone ? '' : enterSends() ? 'Enter to send, Shift+Enter for a new line' : 'Customers, bookings, invoices, your schedule'}</span>
        {/* Stop while the assistant answers, as Claude (Gary, 18 Oct). */}
        {busy
          ? <button type="button" className="chats-ask-send chats-ask-send--stop" aria-label="Stop" title="Stop" onClick={() => stopper.current?.abort()}><StopIcon /></button>
          : <button type="submit" className="chats-ask-send chats-ask-send--icon" disabled={!draft.trim()} aria-label="Ask"><ArrowUpIcon /></button>}
      </div>
    </form>
  );

  if (!turns.length && !loading) {
    return (
      <div className="chats-ask-start">
        <div className="chats-ask-start-inner">
          <DrcAvatar large />
          <h2 className="chats-ask-greeting">{greeting(profile?.first_name)}</h2>
          {inProject && <p className="chats-ask-in-project"><FolderIcon /> New chat in {inProject.name}</p>}
          <p className="chats-ask-sub">I look things up in Sentinel for you. I only show what you are allowed to see, and nothing here goes to customers.</p>
          {composer}
          <div className="chats-ask-suggestions">
            {SUGGESTIONS.map((s) => <button key={s} type="button" onClick={() => void send(s)}>{s}</button>)}
          </div>
        </div>
      </div>
    );
  }

  const title = summary?.title || turns[0]?.question || 'New chat';
  const titleBar = !phone && (
    <div className="chats-ask-titlebar">
      {inProject && <span className="chats-ask-titlebar-project"><FolderIcon />{inProject.name}<span aria-hidden="true">/</span></span>}
      {renaming !== null && summary ? (
        <input className="chats-input chats-ask-titlebar-input" value={renaming} maxLength={200} autoFocus aria-label="Chat name"
          onChange={(e) => setRenaming(e.target.value)}
          onBlur={() => { const t = renaming.trim(); setRenaming(null); if (t) void askActions.rename(summary.id, t); }}
          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setRenaming(null); }} />
      ) : (
        <button type="button" className="chats-ask-titlebar-title" title="Rename" disabled={!summary} onClick={() => summary && setRenaming(summary.title ?? '')}>{title}</button>
      )}
      {summary && (
        <>
          <button type="button" className={`chats-icon-btn chats-ask-titlebar-star${summary.starred_at ? ' chats-ask-titlebar-star--on' : ''}`}
            aria-pressed={!!summary.starred_at} aria-label={summary.starred_at ? 'Unstar' : 'Star'} title={summary.starred_at ? 'Unstar' : 'Star'}
            onClick={() => void askActions.star(summary.id, !summary.starred_at)}><StarIcon filled={!!summary.starred_at} /></button>
          <button type="button" className="chats-icon-btn" aria-label="More" title="More"
            onClick={() => openSheet(<AskChatMenu chat={summary} onRename={() => setRenaming(summary.title ?? '')} onDeleted={() => navigate('/drc', { replace: true })} />)}><MoreIcon /></button>
        </>
      )}
    </div>
  );

  return (
    <>
      {titleBar}
      <div className="chats-ask-thread" ref={thread}
        onScroll={(e) => { const el = e.currentTarget; nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; }}>
        <div className="chats-ask-column">
          {loading && <p className="chats-ask-status">Opening the chat...</p>}
          {turns.map((t, i) => (
            <div key={i} className="chats-ask-turn">
              <div className="chats-ask-user">{t.question}</div>
              {/* The question's own actions, as in the Claude and ChatGPT apps (Gary, 18 Oct). */}
              <div className="chats-ask-acts chats-ask-acts--user">
                <button type="button" title="Copy question" aria-label="Copy question" onClick={() => copy(t.question, 'Question copied')}><CopyIcon /></button>
                <button type="button" title="Edit and ask again" aria-label="Edit and ask again" onClick={() => { setDraft(t.question); box.current?.focus(); }}><EditIcon /></button>
              </div>
              <div className="chats-ask-reply">
                {t.answer && <DrcAnswer text={t.answer} tables={!phone} />}
                {t.step && <p className="chats-ask-status"><DrcAvatar /> {t.step}...</p>}
                {t.error && <p className="chats-drc-error">{t.error}</p>}
              </div>
              {t.done && (t.answer || t.error) && (
                <div className="chats-ask-acts">
                  {t.answer && <button type="button" title="Copy answer" aria-label="Copy answer" onClick={() => copy(plainAnswer(t.answer), 'Answer copied')}><CopyIcon /> Copy</button>}
                  {i === turns.length - 1 && <button type="button" title="Ask the same again" aria-label="Ask the same again" disabled={busy} onClick={() => void send(t.question)}><RetryIcon /> Ask again</button>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="chats-ask-dock">{composer}</div>
    </>
  );
}
